import Search16 from '@carbon/icons-react/es/search/16';
import Editor from '@ckeditor/ckeditor5-core/src/editor';
import { Collapse } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Panel } from 'components/atoms/collapse';
import { Popover } from 'components/atoms/popover';
import { Select } from 'components/atoms/select';
import { TemplateObjectType } from 'data/templates/api';
import { useCKVariablesEvents } from 'hooks/use-ck-variable-events';
import { useVariablesMentionData } from 'queries/variables';

const SelectContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  outline: none;
`;

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: 'initial',
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    padding: 0,
    background: 'none !important',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

const Title = styled.div`
  padding: var(--spacing-05);
  font-weight: var(--font-weight-bold);
`;

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Search16 />
  </components.DropdownIndicator>
);

const StyledSelect = styled(Select)``;

export const StyledVariablesPopover = styled(Popover)`
  &.ant-popover {
    width: 316px;
    height: 316px;
  }
  .ant-popover-content,
  .ant-popover-inner,
  .ant-popover-inner-content {
    height: 100%;
  }
  .ant-popover-inner-content,
  .ozone-select-container {
    display: flex;
    flex-flow: column;
  }
  .ozone-select-container {
    flex: 1;
    overflow: hidden;
    margin-bottom: var(--spacing-05);
  }
  ${StyledSelect} {
    padding-top: var(--spacing-05);
  }
  .ozone-select__option {
    > button,
    .ant-collapse-header {
      padding: var(--spacing-03) var(--spacing-05) !important;

      &.nested-option {
        padding-left: var(--spacing-07) !important;
      }
    }
  }
  .ozone-select__option--is-focused {
    > button,
    .ant-collapse-header {
      background: #deebff !important;
    }
  }

  .fallback-modal-wrap {
    position: relative;

    .ant-modal {
      top: 0;
      padding-bottom: 0;
    }

    .ant-modal-body {
      padding: var(--spacing-05);
    }

    .ant-form-item {
      padding-bottom: 0;
    }

    button {
      padding: 0;
      height: 16px;
      font-size: var(--font-size-sm);
    }
  }
`;

const StyledCollapse = styled(Collapse)`
  background: none;
  .ant-collapse-item {
    border: none;
  }
  .ant-collapse-header {
    padding: 0 !important;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
  }
  .ant-collapse-arrow {
    position: relative !important;
    top: 0 !important;
    padding-top: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const RealtimeValue = styled.span`
  color: var(--gray-40);
  font-size: var(--font-size-sm);
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: right;
`;

const FieldName = styled.span`
  flex: 1;
  text-align: left;
`;

const RealtimeValueRenderer = ({ value }) => {
  return value ? <RealtimeValue>{value}</RealtimeValue> : null;
};

const NestedOption = (props) => {
  const ref: any = useRef();
  const {
    data: option,
    selectProps: { onSelect: onChange, focussed, onHover },
    ...rest
  } = props;

  const { id } = option;

  useEffect(() => {
    if (focussed === id) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focussed, id]);

  const onMouseEnter = useCallback(() => {
    // getting the current parent index from filtered options.
    const selectOptions = rest.options;
    const [parentFieldIndex, childFieldIndex] = option.indexString.split('.');
    const currentParentIndex = selectOptions.findIndex(
      (_option) => _option.indexString === parentFieldIndex,
    );

    if (currentParentIndex !== -1) {
      onHover(`${currentParentIndex}.${childFieldIndex}`);
    }
  }, [onHover, option.indexString, rest.options]);

  return (
    <components.Option data={option} {...rest} isFocused={focussed === id}>
      <Button
        type="text"
        className="nested-option"
        ref={ref}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          height: 'auto',
          background: 'transparent',
        }}
        onClick={() => {
          onChange({
            fieldName: option.value,
            fieldNameForBackend: option.backendValue,
            realTimeValue: option.realTimeValue,
          });
        }}
      >
        <FieldName onMouseEnter={onMouseEnter}>{option.label}</FieldName>
        <RealtimeValueRenderer value={option.realTimeValue} />
      </Button>
    </components.Option>
  );
};

const Option = (props) => {
  const ref: any = useRef();
  const {
    data: option,
    selectProps: {
      onSelect: onChange,
      focussed,
      expandedFields,
      onNestedFieldSelect,
      onHover,
    },
    ...rest
  } = props;

  const { id, nestedOptions: options = [] } = option;
  const hasOptions = useMemo(() => options.length > 0, [options]);

  useEffect(() => {
    if (focussed === id) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focussed, id]);

  const onMouseEnter = useCallback(() => {
    // getting the current index from filtered options.
    const selectOptions = rest.options;
    const currentIndex = selectOptions.findIndex(
      (_option) => _option.indexString === option.indexString,
    );

    if (currentIndex !== -1) {
      onHover(`${currentIndex}`);
    }
  }, [onHover, option.indexString, rest.options]);

  return (
    <components.Option data={option} {...rest} isFocused={focussed === id}>
      <>
        {hasOptions ? (
          <StyledCollapse
            bordered={false}
            expandIconPosition="right"
            activeKey={expandedFields}
          >
            <Panel
              key={id}
              header={
                <Button
                  ref={ref}
                  type="text"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: 'auto',
                    padding: 0,
                    background: 'transparent',
                  }}
                  onClick={() => {
                    onNestedFieldSelect(id);
                  }}
                >
                  <FieldName onMouseEnter={onMouseEnter}>
                    {option.label}
                  </FieldName>
                  <RealtimeValueRenderer value={option.realTimeValue} />
                </Button>
              }
              showArrow={hasOptions}
            >
              <>
                {options.map((_option: any) => (
                  <NestedOption
                    {...props}
                    data={_option}
                    isFocused={focussed === _option.id}
                  />
                ))}
              </>
            </Panel>
          </StyledCollapse>
        ) : (
          <Button
            type="text"
            ref={ref}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              height: 'auto',
              padding: 0,
              background: 'transparent',
            }}
            onClick={() => {
              onChange({
                fieldName: option.value,
                fieldNameForBackend: option.backendValue,
                realTimeValue: option.realTimeValue,
              });
            }}
          >
            <FieldName onMouseEnter={onMouseEnter}>{option.label}</FieldName>
            <RealtimeValueRenderer value={option.realTimeValue} />
          </Button>
        )}
      </>
    </components.Option>
  );
};

export const SimpleVariablesSelect = ({
  onSelect,
  projectId,
  templateType,
  queryInput,
  editor,
  visible,
}: {
  onSelect: (_option) => void;
  projectId?: number;
  templateType?: TemplateObjectType;
  queryInput?: string;
  editor?: Editor;
  visible: boolean;
}) => {
  const variablesOptions = useVariablesMentionData({ projectId, templateType });
  const [focussed, setFocussed] = useState<number | undefined>();
  const [focussedIndex, setFocussedIndex] = useState<string>('0');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [expandedFields, setExpandedFields] = useState<number[]>([]);

  const filteredOptions = useMemo(() => {
    const queryString = queryInput?.replace(/{/, '').toLowerCase();

    if (!queryString || queryString.length <= 0) {
      return variablesOptions;
    }

    return variablesOptions?.filter((_option) =>
      _option.fieldName.toLowerCase().includes(queryString),
    );
  }, [variablesOptions, queryInput]);

  useEffect(() => {
    // when query input changes updating the options list
    // reset focus to the first item in the list.
    setFocussedIndex('0');
  }, [queryInput]);

  const onNestedFieldSelect = useCallback(
    (_fieldId) => {
      if (expandedFields.includes(_fieldId)) {
        setExpandedFields((_fields) =>
          _fields.filter((_field) => _field !== _fieldId),
        );
      } else {
        setExpandedFields((_fields) => [..._fields, _fieldId]);
      }
    },
    [expandedFields],
  );

  const onSelectNext = useCallback(() => {
    if (!filteredOptions) {
      return;
    }
    setFocussedIndex((_index) => {
      const _value = _index ? parseInt(_index, 10) : 0;
      const [parentFieldIndex, childFieldIndex] = _index.split('.');
      const parentField = filteredOptions?.[parentFieldIndex];

      if (!childFieldIndex) {
        if (
          parentField?.nestedOptions?.length > 0 &&
          expandedFields.includes(parentField.id)
        ) {
          return `${_index}.0`;
        }

        return _value + 1 >= filteredOptions?.length ? _index : `${_value + 1}`;
      }

      const childIndex = parseInt(childFieldIndex, 10);

      return childIndex + 1 < parentField.nestedOptions.length
        ? `${parentFieldIndex}.${childIndex + 1}`
        : _value + 1 >= filteredOptions?.length
        ? _index
        : `${_value + 1}`;
    });
  }, [expandedFields, filteredOptions]);

  const onSelectPrevious = useCallback(() => {
    setFocussedIndex((_index) => {
      const _value = _index ? parseInt(_index, 10) : 0;
      const [parentFieldIndex, childFieldIndex] = _index.split('.');
      const parentIndex = parseInt(parentFieldIndex, 10);

      if (!childFieldIndex) {
        const prevField = filteredOptions?.[parentIndex - 1];

        if (
          prevField?.nestedOptions?.length &&
          prevField?.nestedOptions?.length > 0 &&
          expandedFields.includes(prevField.id)
        ) {
          return `${parentIndex - 1}.${prevField?.nestedOptions?.length - 1}`;
        }

        return _value - 1 < 0 ? _index : `${_value - 1}`;
      }
      const childIndex = parseInt(childFieldIndex, 10);

      return childIndex - 1 >= 0
        ? `${parentFieldIndex}.${childIndex - 1}`
        : parentFieldIndex;
    });
  }, [expandedFields, filteredOptions]);

  const onEnter = useCallback(() => {
    if (focussedIndex) {
      const [parentFieldIndex, childFieldIndex] = focussedIndex.split('.');
      const _option = childFieldIndex
        ? filteredOptions?.[parentFieldIndex].nestedOptions?.[childFieldIndex]
        : filteredOptions?.[parentFieldIndex];

      if (_option?.nestedOptions?.length > 0) {
        onNestedFieldSelect(_option.id);
      } else {
        onSelect({
          fieldName: _option.value,
          fieldNameForBackend: _option.backendValue,
          realTimeValue: _option.realTimeValue,
        });
      }
    }
  }, [focussedIndex, onNestedFieldSelect, onSelect, filteredOptions]);

  const handleCKVariablesEvents = useCallback(
    (e) => {
      switch (e.name) {
        case 'variablesPopup:selectNext':
          onSelectNext();
          break;

        case 'variablesPopup:selectPrevious':
          onSelectPrevious();
          break;

        case 'variablesPopup:executeSelected':
          onEnter();
          break;

        default:
          break;
      }
    },
    [onEnter, onSelectNext, onSelectPrevious],
  );

  useCKVariablesEvents(handleCKVariablesEvents, editor);

  useEffect(() => {
    if (focussedIndex && filteredOptions && filteredOptions.length > 0) {
      const [parentFieldIndex, childFieldIndex] = focussedIndex.split('.');
      const parentField = filteredOptions?.[parentFieldIndex];

      if (!childFieldIndex) {
        setFocussed(parentField.id);
      } else {
        setFocussed(parentField.nestedOptions?.[childFieldIndex].id);
      }
    }
  }, [focussedIndex, filteredOptions]);

  const onHover = useCallback((indexString: string) => {
    setFocussedIndex(indexString);
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (!filteredOptions || editor) {
        return;
      }
      switch (event.keyCode) {
        // Enter key
        case 13:
          onEnter();
          break;

        // Up arrow key
        case 38:
          onSelectPrevious();
          break;

        // Down arrow key
        case 40:
          onSelectNext();
          break;

        default:
          break;
      }
    },
    [editor, onEnter, onSelectNext, onSelectPrevious, filteredOptions],
  );

  useEffect(() => {
    if (!editor && visible) {
      // zero timeout is required since focus is lost to cke
      // and need to regain the focus when editor is not passed.
      // this is required for keyboard navigation.
      setTimeout(() => containerRef.current?.focus(), 0);
    }
  }, [editor, visible]);

  return (
    <SelectContainer
      ref={containerRef}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onClick={() => containerRef.current?.focus()}
    >
      <Title>
        <FormattedMessage id="variables.simple.header" />
      </Title>
      <Select
        styles={selectStyles}
        components={{
          DropdownIndicator,
          ClearIndicator: null,
          IndicatorSeparator: null,
          Option,
          Control: () => <></>,
        }}
        menuIsOpen
        autoFocus
        options={filteredOptions}
        onSelect={onSelect}
        focussed={focussed}
        expandedFields={expandedFields}
        onNestedFieldSelect={onNestedFieldSelect}
        onHover={onHover}
      />
    </SelectContainer>
  );
};

export const SimpleVariablesPopover = ({
  visible,
  onSelect,
  projectId,
  attachTo,
  children,
  templateType,
  queryInput,
  editor,
}: {
  visible: boolean;
  onSelect: (value) => void;
  projectId?: number;
  attachTo?: HTMLElement | null;
  children?: any;
  templateType?: TemplateObjectType;
  queryInput?: string;
  editor?: Editor;
}) => {
  useEffect(() => {
    if (!editor) {
      return;
    }
    editor?.editing.view.focus();
  }, [editor]);

  return (
    <StyledVariablesPopover
      className="ck-variables-popup"
      arrowContent={false}
      visible={visible}
      getPopupContainer={attachTo ? () => attachTo as HTMLElement : undefined}
      placement="topLeft"
      content={
        <SimpleVariablesSelect
          onSelect={onSelect}
          projectId={projectId}
          templateType={templateType}
          queryInput={queryInput}
          editor={editor}
          visible={visible}
        />
      }
    >
      {children}
    </StyledVariablesPopover>
  );
};
